/** Keep the customer portrait distinct from the ID and the item photographs. */
export function getCaptureGroups(captures = {}) {
  const entries = Object.entries(captures || {})
    .filter(([, value]) => typeof value === 'string' && value.trim().length > 0)
  const byKey = key => entries.find(([entryKey]) => entryKey === key)?.[1] || null

  return {
    idFront: byKey('id_front'),
    customerPhoto: byKey('customer_photo'),
    itemPhotos: entries
      .filter(([key]) => key.startsWith('item_photo_'))
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([, value]) => value),
    sellerSig: byKey('signature_seller'),
    buyerSig: byKey('signature_buyer'),
  }
}

const IMAGE_FAILURE = 'A contract image could not be loaded. Reload the contract and try again before printing.'
const IMAGE_TIMEOUT = 'Contract images are still loading. Wait for the photos to appear, then try printing again.'

function abortError() {
  const error = new Error('Contract image preparation was cancelled.')
  error.name = 'AbortError'
  return error
}

/**
 * Storage previews can start loading before an img exists, so their wrappers
 * participate through data-contract-image-state. Loaded wrappers mean their
 * images have decoded; all other images (including the logo) must also load.
 * Recheck a ready DOM once before resolving so a just-mounted preview can join.
 */
export function waitForContractImages(root, { signal, timeoutMs = 15000, pollMs = 50 } = {}) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError())
      return
    }
    if (!root || typeof root.querySelectorAll !== 'function') {
      reject(new Error('The contract is not ready to print. Open it again and retry.'))
      return
    }
    if (!Number.isFinite(timeoutMs) || timeoutMs <= 0 || !Number.isFinite(pollMs) || pollMs <= 0) {
      reject(new Error('Contract image waiting requires a positive timeout and polling interval.'))
      return
    }

    let settled = false
    let pollTimer = null
    let timeoutTimer = null
    let previouslyReady = false

    const finish = error => {
      if (settled) return
      settled = true
      clearTimeout(pollTimer)
      clearTimeout(timeoutTimer)
      signal?.removeEventListener('abort', onAbort)
      if (error) reject(error)
      else resolve()
    }
    const onAbort = () => finish(abortError())

    const check = () => {
      if (settled) return
      try {
        const markers = Array.from(root.querySelectorAll('[data-contract-image-state]'))
        const images = Array.from(root.querySelectorAll('img'))
        let ready = true

        for (const marker of markers) {
          const state = marker.getAttribute('data-contract-image-state')
          if (state === 'failed') {
            finish(new Error(IMAGE_FAILURE))
            return
          }
          if (state !== 'loaded') ready = false
        }
        for (const image of images) {
          if (image.complete && !(image.naturalWidth > 0)) {
            finish(new Error(IMAGE_FAILURE))
            return
          }
          if (!image.complete || !(image.naturalWidth > 0)) ready = false
        }

        if (ready && previouslyReady) {
          finish()
          return
        }
        previouslyReady = ready
        pollTimer = setTimeout(check, Math.min(pollMs, timeoutMs))
      } catch {
        // Never expose image URLs, storage tokens, or DOM implementation errors.
        finish(new Error(IMAGE_FAILURE))
      }
    }

    signal?.addEventListener('abort', onAbort, { once: true })
    timeoutTimer = setTimeout(() => finish(new Error(IMAGE_TIMEOUT)), timeoutMs)
    check()
  })
}
